import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { AdminLayout } from '../components/AdminLayout';
import { KPICard } from '../components/KPICard';
import { useProductUsage } from '../hooks/useAdminStats';
import { useAdminFilter } from '../components/AdminSidebar';
import { useTheme } from '@/contexts/ThemeContext';
import {
  ArrowLeftRight,
  Users,
  PieChart,
  Target,
  Wallet,
  DollarSign,
  RefreshCw,
} from 'lucide-react';

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toLocaleString('fr-FR');
}

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M €';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K €';
  return n.toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });
}

export default function ProductUsagePage() {
  const { excludeAdmins } = useAdminFilter();
  const { data, isLoading, error, refetch } = useProductUsage(excludeAdmins);
  const { isDarkMode } = useTheme();

  const transactionsChartOption = useMemo(() => {
    if (!data?.transactions_per_day) return {};
    const days = data.transactions_per_day.map((d) => d.day);
    const counts = data.transactions_per_day.map((d) => d.count);

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: isDarkMode ? '#1f2937' : '#fff',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        textStyle: { color: isDarkMode ? '#f9fafb' : '#111827' },
      },
      grid: { top: 20, right: 20, bottom: 30, left: 50 },
      xAxis: {
        type: 'category',
        data: days,
        axisLine: { lineStyle: { color: isDarkMode ? '#374151' : '#e5e7eb' } },
        axisLabel: {
          color: isDarkMode ? '#9ca3af' : '#6b7280',
          fontSize: 10,
          formatter: (v: string) => v.slice(5),
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: isDarkMode ? '#1f2937' : '#f3f4f6' } },
        axisLabel: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
      },
      series: [
        {
          data: counts,
          type: 'line',
          smooth: true,
          lineStyle: { color: '#16c47f', width: 3 },
          itemStyle: { color: '#16c47f' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#16c47f25' },
                { offset: 1, color: '#16c47f00' },
              ],
            },
          },
        },
      ],
    };
  }, [data, isDarkMode]);

  const banksChartOption = useMemo(() => {
    if (!data?.top_banks) return {};
    const banks = data.top_banks.map((b) => b.bank);
    const counts = data.top_banks.map((b) => b.count);
    const colors = [
      '#16c47f',
      '#3b82f6',
      '#8b5cf6',
      '#f59e0b',
      '#ef4444',
      '#ec4899',
      '#14b8a6',
      '#f97316',
      '#6366f1',
      '#84cc16',
    ];

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: isDarkMode ? '#1f2937' : '#fff',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        textStyle: { color: isDarkMode ? '#f9fafb' : '#111827' },
      },
      grid: { top: 10, right: 20, bottom: 40, left: 120 },
      xAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: isDarkMode ? '#1f2937' : '#f3f4f6' } },
        axisLabel: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
      },
      yAxis: {
        type: 'category',
        data: banks.reverse(),
        axisLine: { lineStyle: { color: isDarkMode ? '#374151' : '#e5e7eb' } },
        axisLabel: { color: isDarkMode ? '#d1d5db' : '#374151', fontSize: 11 },
      },
      series: [
        {
          data: counts.reverse().map((c, i) => ({
            value: c,
            itemStyle: { color: colors[i % colors.length], borderRadius: [0, 6, 6, 0] },
          })),
          type: 'bar',
          barWidth: 20,
        },
      ],
    };
  }, [data, isDarkMode]);

  const categoriesChartOption = useMemo(() => {
    if (!data?.top_categories) return {};
    const palette = [
      '#16c47f',
      '#8b5cf6',
      '#f59e0b',
      '#ef4444',
      '#3b82f6',
      '#ec4899',
      '#14b8a6',
      '#f97316',
      '#6366f1',
      '#84cc16',
    ];

    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: isDarkMode ? '#1f2937' : '#fff',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        textStyle: { color: isDarkMode ? '#f9fafb' : '#111827' },
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          data: data.top_categories.map((c, i) => ({
            name: c.category,
            value: c.count,
            itemStyle: { color: palette[i % palette.length] },
          })),
          label: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 11 },
          emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.1)' } },
        },
      ],
    };
  }, [data, isDarkMode]);

  if (isLoading) {
    return (
      <AdminLayout title="Product Usage" subtitle="Utilisation du produit">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 animate-pulse"
            >
              <div className="w-11 h-11 bg-gray-200 dark:bg-gray-700 rounded-xl mb-4" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-2" />
              <div className="h-7 bg-gray-200 dark:bg-gray-700 rounded w-16" />
            </div>
          ))}
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Product Usage" subtitle="Utilisation du produit">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-8 text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={refetch}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-xl hover:bg-red-200 transition"
          >
            <RefreshCw className="w-4 h-4" /> Réessayer
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Product Usage"
      subtitle="Utilisation du produit"
      action={
        <button
          onClick={refetch}
          className="p-2 rounded-xl text-gray-400 hover:text-[#16c47f] hover:bg-[#16c47f]/10 transition-all"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      }
    >
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <KPICard
          title="Transactions Créées"
          value={formatNumber(data?.total_transactions ?? 0)}
          icon={ArrowLeftRight}
          gradient="from-[#16c47f] to-emerald-600"
        />
        <KPICard
          title="Moy. Trans./Utilisateur"
          value={String(data?.avg_transactions_per_user ?? 0)}
          icon={Users}
          gradient="from-blue-500 to-blue-600"
        />
        <KPICard
          title="Budgets Créés"
          value={formatNumber(data?.total_budgets ?? 0)}
          icon={PieChart}
          gradient="from-violet-500 to-violet-600"
        />
        <KPICard
          title="Objectifs Créés"
          value={formatNumber(data?.total_goals ?? 0)}
          icon={Target}
          gradient="from-amber-500 to-amber-600"
        />
        <KPICard
          title="Comptes Connectés"
          value={formatNumber(data?.total_accounts ?? 0)}
          icon={Wallet}
          gradient="from-pink-500 to-pink-600"
        />
        <KPICard
          title="Argent Suivi"
          value={formatCurrency(data?.total_money_tracked ?? 0)}
          icon={DollarSign}
          gradient="from-cyan-500 to-cyan-600"
        />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Transactions per day */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            Transactions par jour
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">90 derniers jours</p>
          {data?.transactions_per_day?.length ? (
            <ReactECharts option={transactionsChartOption} style={{ height: 280 }} />
          ) : (
            <div className="h-[280px] flex items-center justify-center text-gray-400 text-sm">
              Aucune donnée
            </div>
          )}
        </div>

        {/* Top banks */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            Top 10 Banques
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
            Banques les plus connectées
          </p>
          {data?.top_banks?.length ? (
            <ReactECharts option={banksChartOption} style={{ height: 280 }} />
          ) : (
            <div className="h-[280px] flex items-center justify-center text-gray-400 text-sm">
              Aucune donnée
            </div>
          )}
        </div>
      </div>

      {/* Categories Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Top Catégories</h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
          Catégories de transactions les plus utilisées
        </p>
        {data?.top_categories?.length ? (
          <div className="grid lg:grid-cols-2 gap-6">
            <ReactECharts option={categoriesChartOption} style={{ height: 300 }} />
            <div className="flex flex-col justify-center space-y-3">
              {data.top_categories.map((cat, i) => {
                const total = data.top_categories!.reduce((s, c) => s + c.count, 0);
                const pct = total > 0 ? (cat.count / total) * 100 : 0;
                const colors = [
                  'bg-[#16c47f]',
                  'bg-violet-500',
                  'bg-amber-500',
                  'bg-red-500',
                  'bg-blue-500',
                  'bg-pink-500',
                  'bg-teal-500',
                  'bg-orange-500',
                  'bg-indigo-500',
                  'bg-lime-500',
                ];
                return (
                  <div key={cat.category} className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${colors[i % colors.length]}`} />
                    <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                      {cat.category}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {cat.count}
                    </span>
                    <span className="text-xs text-gray-400 w-12 text-right">{pct.toFixed(1)}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="text-gray-400 text-sm text-center py-8">Aucune donnée disponible</p>
        )}
      </div>
    </AdminLayout>
  );
}
